/**
 * PremiumAnimations.jsx
 * Animation wrapper components for Premium Fleet UI
 *
 * Provides declarative animation components
 */

import React, { forwardRef } from 'react';
import { Box, keyframes } from '@mui/material';
import {
  useEnterAnimation,
  useRipple,
  useCountAnimation,
  useScrollReveal,
} from '../../hooks/useAnimations';

// ============================================================================
// KEYFRAMES (for MUI sx prop)
// ============================================================================

const fadeInKeyframes = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeInUpKeyframes = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInDownKeyframes = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleInKeyframes = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const popInKeyframes = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.9);
  }
  70% {
    transform: scale(1.02);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const slideInLeftKeyframes = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInRightKeyframes = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const rippleKeyframes = keyframes`
  from {
    transform: scale(0);
    opacity: 0.5;
  }
  to {
    transform: scale(4);
    opacity: 0;
  }
`;

// ============================================================================
// FADE IN
// Simple fade in animation wrapper
// ============================================================================

export const FadeIn = forwardRef(({
  children,
  duration = 200,
  delay = 0,
  easing = 'ease-out',
  style,
  ...props
}, ref) => (
  <Box
    ref={ref}
    sx={{
      animation: `${fadeInKeyframes} ${duration}ms ${easing} ${delay}ms both`,
      ...style,
    }}
    {...props}
  >
    {children}
  </Box>
));

FadeIn.displayName = 'FadeIn';

// ============================================================================
// SLIDE UP
// Slide up with fade animation wrapper
// ============================================================================

export const SlideUp = forwardRef(({
  children,
  duration = 200,
  delay = 0,
  distance = 10,
  easing = 'ease-out',
  style,
  ...props
}, ref) => {
  const slideUpKeyframes = keyframes`
    from {
      opacity: 0;
      transform: translateY(${distance}px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `;

  return (
    <Box
      ref={ref}
      sx={{
        animation: `${slideUpKeyframes} ${duration}ms ${easing} ${delay}ms both`,
        ...style,
      }}
      {...props}
    >
      {children}
    </Box>
  );
});

SlideUp.displayName = 'SlideUp';

// ============================================================================
// SLIDE DOWN
// Slide down with fade animation wrapper
// ============================================================================

export const SlideDown = forwardRef(({
  children,
  duration = 200,
  delay = 0,
  distance = 10,
  easing = 'ease-out',
  style,
  ...props
}, ref) => {
  const slideDownKeyframes = keyframes`
    from {
      opacity: 0;
      transform: translateY(-${distance}px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `;

  return (
    <Box
      ref={ref}
      sx={{
        animation: `${slideDownKeyframes} ${duration}ms ${easing} ${delay}ms both`,
        ...style,
      }}
      {...props}
    >
      {children}
    </Box>
  );
});

SlideDown.displayName = 'SlideDown';

// ============================================================================
// SCALE IN
// Scale in with fade animation wrapper
// ============================================================================

export const ScaleIn = forwardRef(({
  children,
  duration = 200,
  delay = 0,
  easing = 'ease-out',
  style,
  ...props
}, ref) => (
  <Box
    ref={ref}
    sx={{
      animation: `${scaleInKeyframes} ${duration}ms ${easing} ${delay}ms both`,
      ...style,
    }}
    {...props}
  >
    {children}
  </Box>
));

ScaleIn.displayName = 'ScaleIn';

// ============================================================================
// POP IN
// Pop in with bounce effect
// ============================================================================

export const PopIn = forwardRef(({
  children,
  duration = 300,
  delay = 0,
  easing = 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  style,
  ...props
}, ref) => (
  <Box
    ref={ref}
    sx={{
      animation: `${popInKeyframes} ${duration}ms ${easing} ${delay}ms both`,
      ...style,
    }}
    {...props}
  >
    {children}
  </Box>
));

PopIn.displayName = 'PopIn';

// ============================================================================
// STAGGER LIST
// List with staggered animation for children
// ============================================================================

export const StaggerList = ({
  children,
  staggerDelay = 50,
  initialDelay = 0,
  animation = 'fadeInUp', // fadeIn, fadeInUp, fadeInDown, scaleIn, slideLeft, slideRight
  duration = 200,
  easing = 'ease-out',
  as: Component = 'div',
  ...props
}) => {
  const animations = {
    fadeIn: fadeInKeyframes,
    fadeInUp: fadeInUpKeyframes,
    fadeInDown: fadeInDownKeyframes,
    scaleIn: scaleInKeyframes,
    slideLeft: slideInLeftKeyframes,
    slideRight: slideInRightKeyframes,
  };

  const selectedAnimation = animations[animation] || animations.fadeInUp;

  return (
    <Box component={Component} {...props}>
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) return child;

        return (
          <Box
            sx={{
              animation: `${selectedAnimation} ${duration}ms ${easing} ${initialDelay + index * staggerDelay}ms both`,
            }}
          >
            {child}
          </Box>
        );
      })}
    </Box>
  );
};

// ============================================================================
// ANIMATED COUNTER
// Animated number counter
// ============================================================================

export const AnimatedCounter = ({
  value,
  duration = 1000,
  decimals = 0,
  prefix = '',
  suffix = '',
  easing = 'easeOut',
  delay = 0,
  formatOptions = {},
}) => {
  const { value: animatedValue, formattedValue } = useCountAnimation(value, duration, {
    decimals,
    easing,
    delay,
  });

  // Custom formatting if provided
  const displayValue = formatOptions.useLocale !== false
    ? animatedValue.toLocaleString(formatOptions.locale, formatOptions)
    : animatedValue.toFixed(decimals);

  return (
    <>
      {prefix}
      {displayValue}
      {suffix}
    </>
  );
};

// ============================================================================
// RIPPLE BOX
// Box with ripple effect on click
// ============================================================================

export const RippleBox = ({
  children,
  onClick,
  rippleColor = 'rgba(255, 255, 255, 0.3)',
  disabled = false,
  component = 'div',
  sx = {},
  ...props
}) => {
  const { ripples, addRipple } = useRipple(rippleColor);

  const handleClick = (e) => {
    if (!disabled) {
      addRipple(e);
      onClick?.(e);
    }
  };

  return (
    <Box
      component={component}
      onClick={handleClick}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        cursor: disabled ? 'default' : 'pointer',
        ...sx,
      }}
      {...props}
    >
      {children}
      {ripples.map((ripple) => (
        <Box
          key={ripple.id}
          sx={{
            position: 'absolute',
            left: ripple.x,
            top: ripple.y,
            width: ripple.size,
            height: ripple.size,
            borderRadius: '50%',
            backgroundColor: ripple.color,
            pointerEvents: 'none',
            animation: `${rippleKeyframes} 600ms ease-out forwards`,
          }}
        />
      ))}
    </Box>
  );
};

// ============================================================================
// ANIMATED PRESENCE
// Show/hide with animation
// ============================================================================

export const AnimatedPresence = ({
  show,
  children,
  animation = 'fade', // fade, scale, slideUp, slideDown, slideLeft, slideRight
  duration = 200,
  unmountOnExit = true,
}) => {
  const { shouldRender, animationState } = useEnterAnimation(show, duration);

  if (unmountOnExit && !shouldRender) return null;

  const getAnimationStyles = () => {
    const baseTransition = `all ${duration}ms ease-out`;

    const animations = {
      fade: {
        entering: { opacity: 0 },
        entered: { opacity: 1 },
        exiting: { opacity: 0 },
        exited: { opacity: 0 },
      },
      scale: {
        entering: { opacity: 0, transform: 'scale(0.95)' },
        entered: { opacity: 1, transform: 'scale(1)' },
        exiting: { opacity: 0, transform: 'scale(0.95)' },
        exited: { opacity: 0, transform: 'scale(0.95)' },
      },
      slideUp: {
        entering: { opacity: 0, transform: 'translateY(10px)' },
        entered: { opacity: 1, transform: 'translateY(0)' },
        exiting: { opacity: 0, transform: 'translateY(10px)' },
        exited: { opacity: 0, transform: 'translateY(10px)' },
      },
      slideDown: {
        entering: { opacity: 0, transform: 'translateY(-10px)' },
        entered: { opacity: 1, transform: 'translateY(0)' },
        exiting: { opacity: 0, transform: 'translateY(-10px)' },
        exited: { opacity: 0, transform: 'translateY(-10px)' },
      },
      slideLeft: {
        entering: { opacity: 0, transform: 'translateX(-10px)' },
        entered: { opacity: 1, transform: 'translateX(0)' },
        exiting: { opacity: 0, transform: 'translateX(-10px)' },
        exited: { opacity: 0, transform: 'translateX(-10px)' },
      },
      slideRight: {
        entering: { opacity: 0, transform: 'translateX(10px)' },
        entered: { opacity: 1, transform: 'translateX(0)' },
        exiting: { opacity: 0, transform: 'translateX(10px)' },
        exited: { opacity: 0, transform: 'translateX(10px)' },
      },
    };

    return {
      transition: baseTransition,
      ...(animations[animation]?.[animationState] || {}),
    };
  };

  return (
    <Box sx={getAnimationStyles()}>
      {children}
    </Box>
  );
};

// ============================================================================
// SCROLL REVEAL
// Reveal animation on scroll into view
// ============================================================================

export const ScrollReveal = ({
  children,
  animation = 'fadeInUp',
  duration = 400,
  delay = 0,
  threshold = 0.1,
  triggerOnce = true,
  ...props
}) => {
  const { ref, isRevealed } = useScrollReveal({ threshold, triggerOnce });

  const animations = {
    fadeIn: fadeInKeyframes,
    fadeInUp: fadeInUpKeyframes,
    fadeInDown: fadeInDownKeyframes,
    scaleIn: scaleInKeyframes,
    slideLeft: slideInLeftKeyframes,
    slideRight: slideInRightKeyframes,
  };

  const selectedAnimation = animations[animation] || animations.fadeInUp;

  return (
    <Box
      ref={ref}
      sx={{
        opacity: isRevealed ? 1 : 0,
        animation: isRevealed
          ? `${selectedAnimation} ${duration}ms ease-out ${delay}ms both`
          : 'none',
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

// ============================================================================
// HOVER LIFT
// Element that lifts on hover
// ============================================================================

export const HoverLift = ({
  children,
  liftAmount = 4,
  shadow = true,
  scale = false,
  duration = 200,
  component = 'div',
  sx = {},
  ...props
}) => (
  <Box
    component={component}
    sx={{
      transition: `transform ${duration}ms ease-out, box-shadow ${duration}ms ease-out`,
      '&:hover': {
        transform: `translateY(-${liftAmount}px)${scale ? ' scale(1.02)' : ''}`,
        boxShadow: shadow ? '0 8px 25px rgba(0, 0, 0, 0.15)' : undefined,
      },
      '&:active': {
        transform: 'translateY(0) scale(0.98)',
      },
      ...sx,
    }}
    {...props}
  >
    {children}
  </Box>
);

// ============================================================================
// SKELETON SHIMMER
// Shimmer effect for loading states
// ============================================================================

const shimmerKeyframes = keyframes`
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
`;

export const SkeletonShimmer = ({
  width = '100%',
  height = 20,
  borderRadius = 4,
  baseColor = 'rgba(255, 255, 255, 0.05)',
  highlightColor = 'rgba(255, 255, 255, 0.1)',
  duration = 1.5,
  ...props
}) => (
  <Box
    sx={{
      width,
      height,
      borderRadius,
      background: `linear-gradient(90deg, ${baseColor} 25%, ${highlightColor} 50%, ${baseColor} 75%)`,
      backgroundSize: '200% 100%',
      animation: `${shimmerKeyframes} ${duration}s ease-in-out infinite`,
    }}
    {...props}
  />
);

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  FadeIn,
  SlideUp,
  SlideDown,
  ScaleIn,
  PopIn,
  StaggerList,
  AnimatedCounter,
  RippleBox,
  AnimatedPresence,
  ScrollReveal,
  HoverLift,
  SkeletonShimmer,
};
